import { User, UserRole } from "../../../entity/User";
import Pair from "../../../types/Pair";
import { Method, ROUTES, URLs } from "../../configuration/Configuration";
import { fetchAPI } from "../../configuration/MainService";
import { AuthResponse, UserDTO, UsersResponse } from "../../dto/User.dto";
import {
  mapToUser,
  mapTOUserDTO,
  mapToUsers,
  mapUserResponseToUsers,
} from "../../mapper/UserMapper";

interface ILoginParams {
  email: string;
  password: string;
  role?: UserRole;
}

export class WSAuthentication {
  private static instance: WSAuthentication;
  private constructor() {}

  static getInstance(): WSAuthentication {
    if (!WSAuthentication.instance) {
      WSAuthentication.instance = new WSAuthentication();
    }
    return WSAuthentication.instance;
  }

  async login(params: ILoginParams): Promise<User> {
    try {
      const response = await fetchAPI<AuthResponse>({
        url: `${ROUTES.LOGIN}`,
        method: Method.POST,
        params,
      });
      if (!response.ok) {
        const errorData = response.data as { error?: string };
        throw new Error(errorData.error || "Login failed");
      }
      return mapTOUserDTO(response.data);
    } catch (error) {
      throw error;
    }
  }

  async register(params: ILoginParams): Promise<User> {
    try {
      const response = await fetchAPI<User>({
        url: `${ROUTES.REGISTER}`,
        method: Method.POST,
        params,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async logout(): Promise<void> {
    try {
      await fetchAPI({
        url: `/auth/logout`,
        method: Method.POST,
        params: {},
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      localStorage.removeItem("token");
    } catch (error) {
      throw error;
    }
  }

  async forgetPassword(email: string): Promise<void> {
    try {
      const response = await fetchAPI<UserDTO>({
        url: `${ROUTES.FORGET_PASSWORD}`,
        method: Method.POST,
        params: { email: email },
      });
      if (!response.ok) {
        const errorData = response.data as { error?: string };
        throw new Error(errorData.error);
      }
    } catch (error) {
      throw error;
    }
  }

  async resetPassword(
    email: string,
    password: string,
    token: string,
  ): Promise<void> {
    try {
      const response = await fetchAPI({
        url: `${ROUTES.RESET_PASSWORD}`,
        method: Method.POST,
        params: { email: email, newPassword: password },
        isAuthenticated: false,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        const errorData = response.data as { error?: string };
        throw new Error(errorData.error);
      }
    } catch (error) {
      throw error;
    }
  }

  async getAllUsers(): Promise<Pair<User[], string>> {
    try {
      const response = await fetchAPI<UsersResponse>({
        url: `${ROUTES.GET_ALL_USERS}`,
        method: Method.GET,
        params: {},
        isAuthenticated: true,
      });

      return mapUserResponseToUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
      throw error;
    }
  }

  async deleteUser(id: string): Promise<void> {}

  /**
   * Get a user by email
   * @param email string
   * @returns User
   */
  async getUserByEmail(email: string): Promise<User | null> {
    try {
      const url = `${ROUTES.GET_USER_BY_EMAIL}${email}`;
      const response = await fetchAPI<UserDTO>({
        url,
        method: Method.GET,
        params: {},
        isAuthenticated: true,
      });
      return response.data ? mapToUser(response.data) : null;
    } catch (error) {
      throw error;
    }
  }

  async resetUserPassword(email: string, password: string): Promise<User> {
    try {
      const url = `${ROUTES.RESET_PASSWORD}`;
      const response = await fetchAPI({
        url,
        method: Method.POST,
        params: { email, newPassword: password },
        isAuthenticated: true,
      });
      if (!response.ok) {
        const errorData = response.data as { error?: string };
        throw new Error(errorData.error);
      }
      return {
        email,
        password,
        role: UserRole.USER,
      };
    } catch (error) {
      throw error;
    }
  }
}
